<script setup lang="ts">
import { useSlots } from 'vue';

defineOptions({
    name: 'EditoPageSurface'
});

const props = withDefaults(defineProps<{
    tag?: string;
    variant?: 'default' | 'card';
    title?: string;
    description?: string;
    headerSeparator?: boolean;
    tone?: 'default' | 'muted';
    padding?: 'default' | 'none';
    overflow?: 'visible' | 'hidden' | 'auto';
}>(), {
    tag: 'section',
    variant: 'default',
    title: '',
    description: '',
    headerSeparator: true,
    tone: 'default',
    padding: 'default',
    overflow: 'visible'
});

const slots = useSlots();
const hasHeader = computed(() => !!(props.title || props.description || slots.title || slots.actions));

const toneClasses = computed(() => props.tone === 'muted'
    ? 'bg-surface-2 dark:bg-surface-2-dark'
    : 'bg-surface dark:bg-surface-dark');

const overflowClasses = computed(() => ({
    visible: 'overflow-visible',
    hidden: 'overflow-hidden',
    auto: 'overflow-auto'
})[props.overflow]);
</script>

<template>
    <component
        :is="props.tag"
        :class="[
            'min-w-0 rounded-[8px] border border-border text-fg dark:border-border-dark dark:text-fg-dark',
            toneClasses,
            props.variant === 'card' ? 'flex h-full flex-col overflow-hidden' : overflowClasses
        ]"
        data-page-surface
        :data-page-surface-variant="props.variant"
    >
        <div
            v-if="hasHeader"
            :class="[
                'relative flex min-w-0',
                props.variant === 'card' ? 'shrink-0 items-start gap-[12px] px-[15px] pt-[15px] pb-[12px]' : [
                    'gap-[10px]',
                    props.description ? 'items-start' : 'items-center',
                    props.padding === 'none' ? 'px-[16px] py-[14px]' : 'px-[15px] pt-[15px] pb-[14px]'
                ]
            ]"
            data-page-surface-header
        >
            <div v-if="props.title || props.description || slots.title" class="min-w-0 flex-1">
                <slot name="title">
                    <IdeoHeading v-if="props.title" tag="h3" class="m-0 truncate">{{ $t(props.title) }}</IdeoHeading>
                </slot>
                <p v-if="props.description" class="m-0 mt-[3px] text-[0.71875rem]/[1.45] font-[400] text-fg-muted dark:text-fg-muted-dark">
                    {{ $t(props.description) }}
                </p>
            </div>
            <div v-if="slots.actions" class="ms-auto flex shrink-0 items-center gap-[6px]">
                <slot name="actions" />
            </div>
            <span v-if="props.headerSeparator" class="absolute inset-x-[15px] border-b border-border-soft dark:border-border-soft-dark" :class="props.variant === 'card' ? 'bottom-[6px]' : 'bottom-0'" aria-hidden="true" data-page-surface-header-separator />
        </div>

        <div :class="[props.variant === 'card' ? 'min-h-0 flex-1 px-[15px] pb-[12px]' : props.padding === 'none' ? 'p-0' : 'p-[15px]', props.variant === 'card' || props.padding !== 'none' ? '[&>.ideo-form-group:last-child]:mb-0' : '']" data-page-surface-content>
            <slot />
        </div>

        <div v-if="slots.footer" :class="props.variant === 'card' ? 'mt-auto flex shrink-0 items-center justify-end gap-[8px] border-t border-border-soft px-[15px] py-[10px] dark:border-border-soft-dark' : 'border-t border-border px-[15px] py-[12px] dark:border-border-dark'" data-page-surface-footer>
            <slot name="footer" />
        </div>
    </component>
</template>
